import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Eye, RotateCcw, Save, Send, MessageCircle } from "lucide-react";

const EMPTY_TEMPLATE = { heading: "", greeting: "", intro: "", footer: "", whatsapp_body: "" };

/** Managers can edit the overdue email/WhatsApp templates and preview sample values. */
export default function ReminderTemplate() {
  const { currentCompany } = useAuth();
  const [tpl, setTpl] = useState(EMPTY_TEMPLATE);
  const [defaults, setDefaults] = useState(EMPTY_TEMPLATE);
  const [previewHtml, setPreviewHtml] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [whatsappPhone, setWhatsappPhone] = useState("");
  const [sampleBalance, setSampleBalance] = useState("250000");
  const [sampleCurrency, setSampleCurrency] = useState("");
  const [saving, setSaving] = useState(false);

  const activeCurrency = sampleCurrency || currentCompany?.base_currency || "SYP";
  const sampleMessage = useMemo(() => {
    const template = tpl.whatsapp_body || defaults.whatsapp_body || "";
    const values = {
      company: currentCompany?.name || "الشركة",
      name: "فاطمة الحلبي",
      balance: Number(sampleBalance || 0).toLocaleString("en-US", { maximumFractionDigits: 2 }),
      currency: activeCurrency,
    };
    return template.replace(/\{(company|name|balance|currency)\}/g, (_, key) => values[key]);
  }, [tpl.whatsapp_body, defaults.whatsapp_body, currentCompany?.name, sampleBalance, activeCurrency]);

  const load = async () => {
    try {
      const { data } = await api.get("/company/reminder-template");
      const mergedDefaults = { ...EMPTY_TEMPLATE, ...(data.defaults || {}) };
      setDefaults(mergedDefaults);
      setTpl({ ...mergedDefaults, ...(data.template || {}) });
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company/reminder-template", tpl);
      toast.success("تم حفظ قوالب البريد وواتساب");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const preview = async (toEmail) => {
    const amount = Number(sampleBalance);
    if (!Number.isFinite(amount) || amount < 0) return toast.error("أدخل مبلغ معاينة صحيحاً وغير سالب");
    try {
      const { data } = await api.post("/company/reminder-template/preview", {
        template: tpl,
        sample_balance: amount,
        sample_currency: activeCurrency,
        ...(toEmail ? { to_email: toEmail } : {}),
      });
      setPreviewHtml(data.html || "");
      if (toEmail && data.preview_only) toast.info("المعاينة محلية في وضع العرض التجريبي ولم يُرسل بريد حقيقي");
      else if (toEmail) toast.success(`تم إرسال معاينة البريد إلى ${toEmail}`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const sendTestEmail = async () => {
    if (!testEmail.trim()) return toast.error("أدخل إيميل الاختبار");
    await preview(testEmail.trim());
  };

  const sendWhatsAppPreview = () => {
    if (!Number.isFinite(Number(sampleBalance)) || Number(sampleBalance) < 0) return toast.error("أدخل مبلغ معاينة صحيحاً وغير سالب");
    let digits = whatsappPhone.replace(/\D/g, "");
    if (digits.startsWith("00")) digits = digits.slice(2);
    if (digits.length < 8) return toast.error("أدخل رقم واتساب كاملاً مع رمز الدولة");
    if (!sampleMessage.trim()) return toast.error("اكتب نص رسالة واتساب أولاً");
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(sampleMessage)}`, "_blank", "noopener,noreferrer");
  };

  const restoreDefaults = () => {
    setTpl({ ...defaults });
    toast.info("استُعيدت القيم الافتراضية — لم تُحفظ بعد");
  };

  return (
    <div className="space-y-6" data-testid="reminder-template-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2"><Mail className="w-6 h-6 text-primary" /> قالب تذكير الرصيد</h1>
          <p className="text-sm text-muted-foreground mt-1">تُرسل تذكيرات البريد أسبوعياً للعملاء ذوي الرصيد المستحق. مبلغ التذكير الفعلي يبقى رصيد العميل المسجل وعملة الشركة.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={restoreDefaults} className="gap-1" data-testid="restore-defaults-btn"><RotateCcw className="w-4 h-4" /> افتراضي</Button>
          <Button variant="outline" onClick={() => preview()} className="gap-1" data-testid="preview-btn"><Eye className="w-4 h-4" /> معاينة البريد</Button>
          <Button onClick={save} disabled={saving} className="gap-1" data-testid="save-tpl-btn"><Save className="w-4 h-4" /> {saving ? "جاري..." : "حفظ"}</Button>
        </div>
      </div>

      <Card className="p-5 space-y-4">
        <div className="text-xs text-muted-foreground leading-6">
          المتغيرات المتاحة في النص: <code className="px-1 bg-muted rounded">{"{company}"}</code>
          <code className="px-1 bg-muted rounded mx-1">{"{name}"}</code>
          <code className="px-1 bg-muted rounded">{"{balance}"}</code>
          <code className="px-1 bg-muted rounded ms-1">{"{currency}"}</code>
        </div>
        <div>
          <Label>عنوان البريد</Label>
          <Input value={tpl.heading || ""} onChange={(e) => setTpl({ ...tpl, heading: e.target.value })} data-testid="tpl-heading" />
        </div>
        <div>
          <Label>التحية</Label>
          <Input value={tpl.greeting || ""} onChange={(e) => setTpl({ ...tpl, greeting: e.target.value })} data-testid="tpl-greeting" />
        </div>
        <div>
          <Label>المقدمة</Label>
          <Textarea rows={3} value={tpl.intro || ""} onChange={(e) => setTpl({ ...tpl, intro: e.target.value })} data-testid="tpl-intro" />
        </div>
        <div>
          <Label>الخاتمة</Label>
          <Textarea rows={2} value={tpl.footer || ""} onChange={(e) => setTpl({ ...tpl, footer: e.target.value })} data-testid="tpl-footer" />
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="flex items-center gap-2 font-bold"><MessageCircle className="w-5 h-5 text-emerald-600" /> قالب رسالة واتساب</div>
        <p className="text-xs text-muted-foreground">يمكنك تعديل النص واستخدام متغيرات العميل والمبلغ والعملة. زر واتساب يفتح الرسالة جاهزة في التطبيق؛ يجب أن تضغط إرسال بنفسك.</p>
        <Textarea rows={5} value={tpl.whatsapp_body || ""} onChange={(e) => setTpl({ ...tpl, whatsapp_body: e.target.value })} data-testid="tpl-whatsapp-body" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-2">
            <Label htmlFor="sample-balance">مبلغ المعاينة</Label>
            <Input id="sample-balance" type="number" min="0" step="any" value={sampleBalance} onChange={(e) => setSampleBalance(e.target.value)} data-testid="reminder-sample-balance" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sample-currency">عملة المعاينة</Label>
            <Input id="sample-currency" maxLength={16} value={activeCurrency} onChange={(e) => setSampleCurrency(e.target.value.toUpperCase())} placeholder="مثال: SYP أو USD" data-testid="reminder-sample-currency" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="whatsapp-phone">رقم واتساب مع رمز الدولة</Label>
            <Input id="whatsapp-phone" type="tel" inputMode="tel" autoComplete="tel" dir="ltr" value={whatsappPhone} onChange={(e) => setWhatsappPhone(e.target.value)} placeholder="مثال: +963 9xxxxxxxx" data-testid="reminder-whatsapp-phone" />
          </div>
        </div>
        <div className="rounded-lg bg-muted/50 p-3 whitespace-pre-wrap text-sm leading-6" dir="auto" data-testid="whatsapp-message-preview">{sampleMessage}</div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={sendWhatsAppPreview} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="send-whatsapp-preview-btn"><MessageCircle className="w-4 h-4" /> فتح المعاينة في واتساب</Button>
        </div>
        <p className="text-xs text-muted-foreground">المبلغ والعملة أعلاه للمعاينة فقط. عند إرسال التذكير عن عميل فعلي، يستخدم النظام رصيده المسجل وعملة الشركة الأساسية لتجنب إرسال قيمة غير صحيحة.</p>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="font-bold">أرسل معاينة البريد إلى إيميلك</div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" className="max-w-md" data-testid="test-email-input" />
          <Button onClick={sendTestEmail} className="gap-1" data-testid="send-test-btn"><Send className="w-4 h-4" /> إرسال المعاينة</Button>
        </div>
      </Card>

      {previewHtml && (
        <Card className="p-2" data-testid="preview-card">
          <div className="text-xs font-bold p-3 border-b border-border">معاينة البريد (بالمبلغ والعملة المحددين أعلاه)</div>
          <iframe title="preview" srcDoc={previewHtml} className="w-full h-[520px] border-0" />
        </Card>
      )}
    </div>
  );
}
